import * as React from "react";
import { useOutsideClick } from "@chakra-ui/react";
import { format } from "date-fns";
import { isEmpty } from "lodash-es";
import { useAuth, useOnline, useQueryParams, useSelector } from "duck";
import { MedicalCasesQueryVariables, useMedicalCasesQuery } from "requests";
import { SelectionItem } from "./types";
import { filterStoredCases, getFilterData } from "./utils";

export const useListQueryVariables = (): MedicalCasesQueryVariables => {
  const [{ user }] = useAuth();
  const [params] = useQueryParams();

  const allCases = useSelector(({ cases }) => cases);

  return React.useMemo(() => {
    const excludeIds = Object.values(allCases)
      .filter(medCase => medCase.userId === user._id)
      .filter(medCase => medCase.status === "has_offline_changes")
      .map(medCase => medCase.data._id);
    return {
      order: [{ name: "ASC" }],
      filter: getFilterData(params, excludeIds),
      page: params.pagination?.page || 1,
      itemsPerPage: 100
    };
  }, [params, allCases, user]);
};

export const useInitFilters = () => {
  const [ready, setReady] = React.useState(false);
  const [params, setParams] = useQueryParams();

  React.useEffect(() => {
    if (!ready && isEmpty(params?.filter)) {
      setParams({ filter: { date: format(new Date(), "MM/dd/y") } });
    }
    setReady(true);
  }, [params?.filter, ready]);
  return { ready };
};

export const useOnlineCases = ({ skip }: { skip: boolean }) => {
  const online = useOnline();
  const variables = useListQueryVariables();

  const { data, loading } = useMedicalCasesQuery({
    variables,
    fetchPolicy: "cache-and-network",
    skip: skip || !online
  });

  return {
    pageNumber: variables.page,
    cases: data?.medicalCases?.collection,
    loading,
    totalCount: data?.medicalCases?.paginationInfo?.totalCount || 0
  };
};

export const useOfflineCases = () => {
  const [{ user }] = useAuth();
  const [{ filter = {}, search }] = useQueryParams();

  const allUserCases = useSelector(({ cases }) =>
    Object.values(cases).filter(medCase => medCase.userId === user._id)
  );

  const offlineCases = allUserCases
    .filter(medCase => medCase.status === "offline")
    .map(medCase => medCase.data);

  const needSyncCases = allUserCases
    .filter(medCase => medCase.status === "has_offline_changes")
    .map(medCase => medCase.data);

  const cachedCases = allUserCases
    .filter(medCase => medCase.status === "normal")
    .map(medCase => medCase.data);

  return {
    offlineCases: filterStoredCases(offlineCases, filter, search),
    needSyncCases: filterStoredCases(needSyncCases, filter, search),
    cachedCases: filterStoredCases(cachedCases, filter, search)
  };
};

export const useSelection = () => {
  const variables = useListQueryVariables();

  const [selection, setSelection] = React.useState<SelectionItem[]>([]);

  // TODO: Find a more reliable way to manage the selection.
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  useOutsideClick({
    ref: containerRef,
    handler(event: any) {
      const modal = document.querySelector(".chakra-modal__content-container");
      const clickInsideModal = modal?.contains(event.target);

      if (!event.shiftKey && !clickInsideModal) {
        setSelection([]);
      }
    }
  });

  const onContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const actionsButton = document.querySelector(".chakra-menu__menu-button");
    const actionsDropdown = document.querySelector(".chakra-menu__menu-list");
    const clickInsideActions =
      actionsButton?.contains(event.target as any) ||
      actionsDropdown?.contains(event.target as any);

    if (clickInsideActions) {
      return;
    }
    const caseNodes = document.querySelectorAll("[data-id='medical-case']");
    const notClickOnCase = Array.from(caseNodes).every(
      caseNode => !caseNode.contains(event.target as any)
    );
    if (notClickOnCase && !event.shiftKey) {
      setSelection([]);
    }
  };

  const onCaseClick = (
    item: SelectionItem,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
    const ctrlPressed = !isMac && event.ctrlKey;
    const cmdPressed = isMac && event.metaKey;

    // Do not prevent the user from opening a case in a new tab using keyboard shortcut.
    if (ctrlPressed || cmdPressed) {
      return;
    }

    event.preventDefault();

    const shiftPressed = event.shiftKey;
    const isSelected = !!selection.find(mc => mc._id === item._id);

    const newSelected = isSelected ? [] : [item];
    const newSelectedShift = isSelected
      ? selection.filter(prev => prev._id !== item._id)
      : [...selection, item];

    setSelection(shiftPressed ? newSelectedShift : newSelected);
  };

  React.useEffect(() => {
    setSelection([]);
  }, [variables.filter]);

  const reset = () => {
    setSelection([]);
  };

  return {
    selection,
    reset,
    onCaseClick,
    containerProps: {
      ref: containerRef,
      onClick: onContainerClick
    }
  };
};
